import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Input, TextArea, Select, toast } from '../../components/common';
import { departmentApi } from '../../services/api';

export default function DepartmentForm({ department, onSuccess, onCancel }) {
    const isEditing = !!department;

    const [form, setForm] = useState({
        code: '',
        name: '',
        description: '',
        parentId: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (department) {
            setForm({
                code: department.code || '',
                name: department.name || '',
                description: department.description || '',
                parentId: department.parentId || ''
            });
        }
    }, [department]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? departmentApi.update(department.id, data)
            : departmentApi.create(data),
        onSuccess: () => {
            toast.success(`Department ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save department');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.code) newErrors.code = 'Code is required';
        if (!form.name) newErrors.name = 'Name is required';
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join(', ');
            toast.error(`Please fix the following errors: ${errorMessages}`);
            return false;
        }
        return true;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate({
            ...form,
            parentId: form.parentId || null
        });
    };

    return (
        <form onSubmit={handleSubmit} className="department-form">
            <div className="form-stack">
                <Input
                    name="code"
                    label="Department Code"
                    placeholder="e.g., IT, FIN, HR"
                    value={form.code}
                    onChange={handleChange}
                    error={errors.code}
                    disabled={isEditing}
                    required
                />
                <Input
                    name="name"
                    label="Department Name"
                    placeholder="Enter department name"
                    value={form.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                />
                <TextArea
                    name="description"
                    label="Description"
                    placeholder="Brief description of the department"
                    value={form.description}
                    onChange={handleChange}
                    rows={3}
                />
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    {isEditing ? 'Update' : 'Create'} Department
                </Button>
            </div>
        </form>
    );
}

DepartmentForm.propTypes = {
    department: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
